import type { CanonicalToken } from '../localized-defaults';
import type { MessageTemplateDto } from '../message-templates';
import { templateTokens } from '../sms/template-token';
import type { LocationType } from './appointment-types.constants';

export type LocationHintTemplate = Pick<
  MessageTemplateDto,
  | 'confirmation_enabled'
  | 'confirmation_message'
  | 'reminder_enabled'
  | 'reminder_message'
  | 'follow_up_enabled'
  | 'follow_up_message'
>;

export type MissingLocationTokenInput = {
  locationType: LocationType;
  template: LocationHintTemplate | null | undefined;
};

export function findMissingLocationToken({
  locationType,
  template,
}: MissingLocationTokenInput): CanonicalToken | null {
  if (locationType === 'none' || !template) return null;
  const tokens = locationTokens(locationType);
  const isUsed = tokens.some((token) =>
    templateMessagesUseToken(template, token),
  );
  return isUsed ? null : tokens[0];
}

/** Whether any enabled message uses the token, by canonical name or any language's alias. */
export function templateMessagesUseToken(
  template: LocationHintTemplate,
  token: CanonicalToken,
): boolean {
  return enabledTemplateMessages(template).some((body) =>
    templateTokens(body).has(token),
  );
}

/** The hint names the first token. The Meet link is also written into the Google Event location (owner D3). */
function locationTokens(locationType: LocationType): CanonicalToken[] {
  return locationType === 'google_meet'
    ? ['videolink', 'eventlocation']
    : ['eventlocation'];
}

function enabledTemplateMessages(template: LocationHintTemplate): string[] {
  return [
    template.confirmation_enabled ? template.confirmation_message : null,
    template.reminder_enabled ? template.reminder_message : null,
    template.follow_up_enabled ? template.follow_up_message : null,
  ].filter((body): body is string => Boolean(body));
}
